import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import MethodologyDisclaimer from "../components/MethodologyDisclaimer";
import Footer from "../components/Footer";
import BrandMark from "../components/BrandMark";
import { Copy, Download, ArrowLeft, AlertTriangle } from "lucide-react";

const INDEX_META = {
  investor: { title: "Инвесторский интерес", color: "var(--idx-investor)", num: "01" },
  corporate: { title: "Корпоративные пилоты и внедрения", color: "var(--idx-corporate)", num: "02" },
  institutional: { title: "Поддержка институтов развития", color: "var(--idx-institutional)", num: "03" },
};

export default function Result({ shared = false }) {
  const { id, token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const url = shared ? `/api/share/${token}` : `/api/submissions/${id}/result`;
    api.get(url).then((r) => setData(r.data)).catch((e) => setError(e.message));
  }, [id, token, shared]);

  if (error) return <div className="min-h-screen flex items-center justify-center label-mono text-destructive">Ошибка: {error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center label-mono">Загрузка…</div>;

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `diagnostic-${data.submission_id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = shared && data.share_token
      ? `${base}/api/share/${data.share_token}/result.pdf`
      : `${base}/api/submissions/${data.submission_id}/result.pdf`;
    window.open(url, "_blank", "noreferrer");
  };

  const copyShare = async () => {
    if (!data.share_token) return;
    const url = `${window.location.origin}/share/${data.share_token}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const lowConfidence = data.confidence === "low";

  return (
    <div className="min-h-screen">
      <header className="border-b border-black/10">
        <div className="max-w-screen-xl mx-auto px-8 py-5 flex items-center justify-between">
          <BrandMark />
          <div className="flex items-center gap-3">
            {!shared && (
              <Link to="/" className="btn-ghost" data-testid="result-home-link"><ArrowLeft size={14} /> Главная</Link>
            )}
            {data.share_token && (
              <button className="btn-secondary" onClick={copyShare} data-testid="result-share-btn">
                <Copy size={14} /> {copied ? "Скопировано" : "Поделиться"}
              </button>
            )}
            <button className="btn-primary" onClick={downloadPdf} data-testid="result-pdf-btn">
              <Download size={14} /> PDF
            </button>
            <button className="btn-ghost" onClick={downloadJson} data-testid="result-download-btn">
              <Download size={14} /> JSON
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-screen-xl mx-auto px-8 py-16 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-8">
          <div className="label-mono">ДИАГНОСТИЧЕСКИЙ ПРОФИЛЬ</div>
          <h1 className="mt-3 text-4xl md:text-6xl tracking-tight font-medium" data-testid="result-profile-label">
            {data.profile_label}
          </h1>
          <p className="mt-4 text-foreground/70 leading-relaxed max-w-3xl">
            Ниже — три независимых индекса, не сводимые в&nbsp;один балл. Каждый показывает, насколько
            параметры проекта похожи на признаки, чаще связанные с&nbsp;конкретным типом интереса.
          </p>
        </div>
        <div className="md:col-span-4 space-y-2">
          <div className="label-mono">{data.founder?.startup_name}</div>
          <div className="text-xs text-foreground/60 mono">scoring: {data.scoring_profile_name} · {data.scoring_profile_version}</div>
          <div className="text-xs text-foreground/60 mono">questionnaire: {data.questionnaire_version} ({data.questionnaire_type || "mvp"})</div>
          <div className="text-xs text-foreground/60 mono">ai_extraction: {data.ai_extraction_status}</div>
          {data.deck && (
            <div className="text-xs text-foreground/60 mono" data-testid="result-deck-info">
              deck: {data.deck.filename} · {data.deck.pages} стр
              {typeof data.deck.deck_quality_score === "number" && data.deck.extraction_status === "ok" && (
                <> · q {data.deck.deck_quality_score}/5</>
              )}
            </div>
          )}
        </div>
      </section>

      {lowConfidence && (
        <div className="max-w-screen-xl mx-auto px-8 mb-8">
          <div className="grid-cell border border-destructive/40 bg-destructive/5 flex items-start gap-3" data-testid="low-confidence-note">
            <AlertTriangle className="text-destructive mt-1" size={18} />
            <div className="text-sm">
              <div className="font-medium">Пониженная достоверность</div>
              <div className="text-foreground/70">
                Часть данных временно не&nbsp;была обработана ИИ-моделью ({data.ai_extraction_status}).
                Расчёт может быть уточнён при повторном запуске или после загрузки презентации.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Score cards */}
      <section className="max-w-screen-xl mx-auto px-8">
        <div className="grid md:grid-cols-3 border border-black/10">
          {Object.keys(INDEX_META).map((key) => (
            <ScoreCard key={key} keyName={key} idx={data.indices[key]} components={data.components[key]} />
          ))}
        </div>
      </section>

      {/* Strengths and gaps */}
      <section className="max-w-screen-xl mx-auto px-8 mt-16 grid md:grid-cols-2 gap-8">
        <div>
          <div className="label-mono">СИЛЬНЫЕ СТОРОНЫ</div>
          <ul className="mt-4 space-y-2" data-testid="strengths-list">
            {(data.strengths || []).length === 0 && <li className="text-foreground/50 text-sm">Не выявлено явных сильных сигналов.</li>}
            {(data.strengths || []).map((s, i) => (
              <li key={i} className="border-b border-black/10 py-2 text-sm">{s}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="label-mono">ЗОНЫ РОСТА</div>
          <ul className="mt-4 space-y-2" data-testid="gaps-list">
            {(data.gaps || []).length === 0 && <li className="text-foreground/50 text-sm">Существенных пробелов не выявлено.</li>}
            {(data.gaps || []).map((s, i) => (
              <li key={i} className="border-b border-black/10 py-2 text-sm">{s}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Recommendations */}
      <section className="max-w-screen-xl mx-auto px-8 mt-16">
        <div className="label-mono">РЕКОМЕНДАЦИИ НА 90 ДНЕЙ</div>
        <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="recommendations-list">
          {(data.recommendations || []).map((r, i) => (
            <div key={r.rule_id || i} className="grid-cell">
              <div className="flex items-baseline gap-3">
                <div className="label-mono">{String(i + 1).padStart(2, "0")} · {r.priority?.toUpperCase()} · {labelIndex(r.index)}</div>
              </div>
              <h3 className="mt-2 text-lg font-medium leading-snug">{r.title}</h3>
              {r.why && <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{r.why}</p>}
              {r.action_90_days && (
                <div className="mt-4 text-sm">
                  <div className="label-mono mb-1">90 ДНЕЙ</div>
                  <p className="text-foreground/85 leading-relaxed">{r.action_90_days}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-8 mt-16 mb-16">
        <MethodologyDisclaimer />
      </section>

      <Footer />
    </div>
  );
}

function ScoreCard({ keyName, idx, components }) {
  const meta = INDEX_META[keyName];
  return (
    <div className="border-r border-black/10 last:border-r-0 p-8" data-testid={`score-card-${keyName}`}>
      <div className="flex items-baseline justify-between">
        <div className="label-mono">INDEX {meta.num}</div>
        <div className="label-mono">{idx.band.label}</div>
      </div>
      <div className="text-7xl md:text-8xl font-medium mono mt-4 leading-none" data-testid={`score-value-${keyName}`}>
        {idx.value}
      </div>
      <div className="mt-6 score-bar">
        <span style={{ width: `${idx.value}%`, background: meta.color }} />
      </div>
      <h3 className="mt-6 text-xl font-medium leading-snug">{meta.title}</h3>
      <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{idx.band.interpretation}</p>
      <details className="mt-6">
        <summary className="label-mono cursor-pointer">Компоненты</summary>
        <ul className="mt-3 space-y-2">
          {Object.entries(components || {}).map(([cname, c]) => (
            <li key={cname} className="text-xs">
              <div className="flex items-center justify-between">
                <span>{ru(cname)}</span>
                <span className="mono text-foreground/60">{c.answered ? c.average_5.toFixed(1) : "—"}/5</span>
              </div>
              <div className="h-1 bg-muted mt-1 relative overflow-hidden">
                <span className="absolute top-0 left-0 h-full" style={{ width: `${(c.average_5 / 5) * 100}%`, background: meta.color, opacity: c.answered ? 1 : 0.25 }} />
              </div>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function labelIndex(s) {
  const m = { investor: "ИНВЕСТОР", corporate: "КОРПОРАЦИЯ", institutional: "ИНСТИТУТ", all: "ОБЩИЕ" };
  return m[s] || s?.toUpperCase();
}

const RU_COMPONENTS = {
  stage_product: "Стадия и продукт", revenue_growth: "Выручка и рост", customers_sales: "Клиенты и продажи",
  market_scalability: "Рынок и масштабируемость", team: "Команда", investor_readiness: "Инвестиционная готовность",
  ip_barriers: "IP и барьеры", risk_reduction: "Снижение рисков", deck_quality: "Качество презентации", business_value: "Бизнес-ценность",
  pilots_adoption: "Пилоты и внедрения", measurable_effect: "Измеримый эффект",
  integration_readiness: "Готовность к интеграции", b2b_b2g_fit: "B2B/B2G применимость",
  industry_fit: "Отраслевой fit", team_delivery: "Команда поставки", implementation_risk: "Риск внедрения",
  tech_novelty: "Технологическая новизна", priority_alignment: "Соответствие приоритетам",
  rnd_ip_science: "R&D, IP, наука", grant_readiness: "Грантовая готовность", impact: "Эффект",
  feasibility_stage: "Реализуемость и стадия", team_partners: "Команда и партнёры", documentation: "Документация",
};

const ru = (k) => RU_COMPONENTS[k] || k;
